import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { Certificate, GraduationCap } from "@phosphor-icons/react";
import { Section, Pill, fmtD } from "./hrUtils";
import { TONE_CLASSES } from "../assets/assetUtils";

const STATE_TONE = { expired: TONE_CLASSES.overdue, expiring: TONE_CLASSES.soon, valid: TONE_CLASSES.ok };
const stateLabel = (q) => (q.state === "expired" ? `Expired ${fmtD(q.expires_at)}` : q.state === "expiring" ? `Expires in ${q.days} d` : q.expires_at ? `Valid to ${fmtD(q.expires_at)}` : "No expiry");

/** Competency-matrix tickets and internal course results for the login linked to this HR record (read-only mirror of H&S). */
export function TrainingCard({ emp, hr }) {
  const [t, setT] = useState(null);
  useEffect(() => { API.get(`/hr/employees/${emp.id}/training`).then((r) => setT(r.data)).catch(() => setT({ linked: false, qualifications: [], courses: [] })); }, [emp.id]);
  return (
    <Section title="Training & tickets" icon={Certificate} testid="hr-card-training" action={hr && t?.linked && <Link to="/health-safety?tab=competency" className="text-xs text-primary underline" data-testid="hr-training-matrix">Competency matrix</Link>}>
      {!t ? <p className="text-xs text-muted-foreground">Loading…</p> : !t.linked ? (
        <p className="text-sm text-muted-foreground" data-testid="hr-training-unlinked">Link this record to a login (Employment & pay) to pull their H&S tickets and course results through.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="hr-training-summary">
            <Pill tone={TONE_CLASSES.ok}>{t.summary.valid} valid</Pill>
            <Pill tone={t.summary.expiring ? TONE_CLASSES.soon : TONE_CLASSES.none}>{t.summary.expiring} expiring</Pill>
            <Pill tone={t.summary.expired ? TONE_CLASSES.overdue : TONE_CLASSES.none}>{t.summary.expired} expired</Pill>
          </div>
          {t.qualifications.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="hr-training-empty">No tickets or cards on the competency matrix yet.</p> : (
            <ul className="divide-y divide-border text-sm" data-testid="hr-training-quals">{t.qualifications.map((q) => (
              <li key={q.id} className="py-2 flex items-center justify-between gap-2" data-testid={`hr-training-qual-${q.id}`}>
                <div className="min-w-0"><p className="font-medium truncate">{q.type_name}</p><p className="text-xs text-muted-foreground">{q.reference ? `${q.reference} · ` : ""}issued {fmtD(q.issued_at)}{q.source === "training" ? " · internal course" : ""}</p></div>
                <Pill tone={STATE_TONE[q.state]}>{stateLabel(q)}</Pill>
              </li>
            ))}</ul>
          )}
          {t.courses.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><GraduationCap size={12} /> Internal courses</p>
              <ul className="divide-y divide-border text-sm" data-testid="hr-training-courses">{t.courses.map((c) => (
                <li key={c.id} className="py-1.5 flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="truncate">{c.course_title}</p><p className="text-xs text-muted-foreground">{c.status === "passed" ? `Passed ${fmtD(c.passed_at)} · ${c.score}%` : c.due ? `Due ${fmtD(c.due)}` : "Assigned"}</p></div>
                  <Pill tone={c.status === "passed" ? TONE_CLASSES.ok : c.overdue ? TONE_CLASSES.overdue : TONE_CLASSES.soon}>{c.status === "passed" ? "Passed" : c.overdue ? "Overdue" : "In progress"}</Pill>
                </li>
              ))}</ul>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
